/**
 * THE CALL SITE for `FreeEnrolmentDialog` — one control, used from two surfaces.
 *
 * The dialog was built, the routes exist, the audit action exists, and nothing could open it.
 * This is the missing entry point, and it is ONE component rather than a copy on each surface
 * for the same reason `LearnerRoster` is one panel: the operator's task is identical, and two
 * copies of "may I remove this access" would eventually answer differently.
 *
 * WHAT IT DRAWS, AND THE FOUR STATES ARE FOUR DIFFERENT FACTS
 * ----------------------------------------------------------
 *   payment ops absent here    nothing at all. `THINLMS_PAYMENT_OPS_ENABLED` is off, so there is
 *                              no authority anybody could grant and no sentence worth printing.
 *   gate refuses               the server's own sentence, and NO BUTTON. Not a disabled one —
 *                              R8.3.2 / AC-266: the write affordance is absent, so there is
 *                              nothing to re-enable in dev tools.
 *   gate answers writable      the button, which opens the dialog.
 *   gate not yet known         nothing but the pending state. An unanswered query is not
 *                              permission; `writeGate(undefined)` is `unknown` and stays
 *                              read-only.
 *
 * WHY IT ASKS FOR THE GATE ITSELF rather than taking it as a prop. Both call sites are screens
 * about people and pathways, not about money: `AdminPaths` reads the pathway authoring API and
 * `PersonDetail` reads the academy's member list, and neither has any reason to hold a
 * payment-ops payload. `useOpsGate` is one cached read shared by every instance on the page —
 * the query key is the same for all of them, so ten roster rows still cost one request.
 *
 * TWO AUTHORITIES, BOTH REQUIRED, AND THEY ARE NOT THE SAME QUESTION. The caller decides whether
 * this operator may act on the PATHWAY (`canWrite(path, writable_orgs)` on the pathways screen,
 * the member list's own `canManage` on the person screen) and simply does not render this at all
 * when they may not. This component decides whether they may act on the academy's PAYMENT
 * OPERATIONS. The server re-checks both — `free_enrolment.preview` asserts authority over the
 * pathway's org and the view asserts the payment-ops scope — so neither check here is the
 * boundary; they decide what to draw.
 */

import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Badge, Banner, Button, Icon } from '../../shared/ui';
import { may, PAYMENT_OPS_KEY, type WireFreeCancelResult } from '../api/paymentOps';
import { FreeEnrolmentDialog } from './FreeEnrolmentDialog';
import { useOpsGate } from './useOpsGate';

/** The verb, as `_controls()` spells it. Not a local string — the gate lists this exact name. */
export const CANCEL_FREE_ENROLMENT = 'cancel_free_enrolment';

export function FreeEnrolmentAction({
  pathwayKey,
  username,
  email,
  /** What to invalidate after a successful cancellation. The caller owns its own cache. */
  onDone,
  /** Rendered instead of the button's own label, where a surface needs a shorter one. */
  label = 'Cancel free enrolment',
  /** Shown above the button on surfaces with room for it, e.g. the person page. */
  children,
}: {
  pathwayKey: string;
  username?: string;
  email?: string;
  onDone?: (result: WireFreeCancelResult) => void;
  label?: string;
  children?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState<WireFreeCancelResult | null>(null);
  const queryClient = useQueryClient();

  const { gate, dark, loading } = useOpsGate();

  // Nothing at all. See the header: absent is not refused.
  if (dark) return null;
  if (loading) return <span className="admin-muted">Checking what you may do here…</span>;

  if (!may(gate, CANCEL_FREE_ENROLMENT)) {
    // The gate's own reason, verbatim. `writeGate` has already turned the three refusal codes
    // into the three sentences with three different next actions.
    return (
      <Banner tone="info">
        <p>{'reason' in gate ? gate.reason : ''}</p>
        <p>
          A free enrolment can still be removed from the learning path&rsquo;s own learner list,
          but that removes the pathway only, writes no audit row, and does not look at the
          courses the pathway gave the learner.
        </p>
      </Banner>
    );
  }

  const settle = (result: WireFreeCancelResult) => {
    setOpen(false);
    setOutcome(result);
    // The whole payment-ops tree, because a cancellation writes an audit row and moves grant
    // state, and this component does not know which of those a caller is showing.
    void queryClient.invalidateQueries({ queryKey: PAYMENT_OPS_KEY });
    onDone?.(result);
  };

  return (
    <>
      {children}
      {/* THE OUTCOME SURVIVES THE DIALOG CLOSING, and a partial says so here too. The dialog's
          own summary disappears with it, and "partly done" is exactly the answer somebody
          needs to still be able to see once they are back on the list. */}
      {outcome &&
        (outcome.not_confirmed.length > 0 ? (
          <Banner tone="warning">
            <strong>Partly cancelled.</strong> {outcome.revoked.length} removed,{' '}
            {outcome.not_confirmed.length} left in place because they were not confirmed.
          </Banner>
        ) : (
          <Banner tone="success">
            <strong>Cancelled.</strong> {outcome.revoked.length}{' '}
            {outcome.revoked.length === 1 ? 'item was' : 'items were'} removed.{' '}
            {outcome.retained.length > 0 && (
              <>
                {outcome.retained.length}{' '}
                {outcome.retained.length === 1 ? 'was' : 'were'} kept, because the learner holds{' '}
                {outcome.retained.length === 1 ? 'it' : 'them'} independently of this enrolment.
              </>
            )}
          </Banner>
        ))}

      <Button variant="ghost" onClick={() => setOpen(true)}>
        <Icon name="money_off" />
        {label}
      </Button>

      {open && (
        <FreeEnrolmentDialog
          pathwayKey={pathwayKey}
          username={username}
          email={email}
          onClose={() => setOpen(false)}
          onDone={settle}
        />
      )}
    </>
  );
}

/**
 * The badge the roster row shows next to the plain `Remove` button.
 *
 * Named separately because the two controls do genuinely different things and a row that offers
 * both must say so: `Remove` withdraws the pathway enrolment through the pathways API and stops
 * there; this operation withdraws it, decides each member course on the evidence, keeps what the
 * learner owns independently, and writes a permanent audit row.
 */
export function FreeEnrolmentAuditedBadge() {
  return <Badge tone="neutral">audited</Badge>;
}
